const DEFAULT_SCREENING_SPECIALTY_SLUG = 'psychiatry'
const DEFAULT_SCREENING_SPECIALTY_LABEL = 'Psychiatry (Default)'

function humanizeSlug(slug) {
  return String(slug || '')
    .trim()
    .split(/[-_ ]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export function normalizeScreeningSpecialtyBucket(value) {
  const normalized = String(value || '').trim().toLowerCase()
  if (!normalized || normalized === DEFAULT_SCREENING_SPECIALTY_SLUG) {
    return ''
  }
  return normalized
}

export function normalizeScreeningSpecialtyForPayload(value) {
  const normalized = normalizeScreeningSpecialtyBucket(value)
  return normalized || undefined
}

export function formatScreeningSpecialtyLabel(value) {
  const normalized = normalizeScreeningSpecialtyBucket(value)
  return normalized ? humanizeSlug(normalized) : DEFAULT_SCREENING_SPECIALTY_LABEL
}

export function buildScreeningTargetSpecialtyOptions(targetSpecialties) {
  const seen = new Set([''])
  const options = [
    {
      id: 'screening-specialty-default',
      slug: '',
      title: DEFAULT_SCREENING_SPECIALTY_LABEL,
      screeningEnabled: true,
    },
  ]

  ;(targetSpecialties || []).forEach((specialty) => {
    const slug = normalizeScreeningSpecialtyBucket(specialty?.slug || specialty?.id)
    if (seen.has(slug)) {
      return
    }
    if (specialty?.screeningEnabled === false) {
      return
    }
    seen.add(slug)
    options.push({
      ...specialty,
      slug,
      title: specialty?.title || specialty?.name || humanizeSlug(slug),
    })
  })

  return options
}

export function normalizeScreeningSpecialtyConstraint(value) {
  const normalizedBucket = normalizeScreeningSpecialtyBucket(value)
  return normalizedBucket || DEFAULT_SCREENING_SPECIALTY_SLUG
}

export function normalizeScreeningSpecialtyConstraintList(values) {
  if (!Array.isArray(values) || values.length === 0) {
    return []
  }
  const normalized = new Set()
  values.forEach((value) => {
    const cleaned = String(value || '').trim()
    if (!cleaned) {
      return
    }
    normalized.add(normalizeScreeningSpecialtyConstraint(cleaned))
  })
  return Array.from(normalized)
}

function getClinicConfiguredSpecialties(source) {
  if (!source || typeof source !== 'object') {
    return []
  }
  const values = source?.targetSpecialitySlugs ?? source?.targetSpecialtySlugs
  return Array.isArray(values) ? values : []
}

export function resolveClinicAvailableSpecialtySlugs(clinicLocation, clinicBrand) {
  const locationSpecialties = normalizeScreeningSpecialtyConstraintList(
    getClinicConfiguredSpecialties(clinicLocation),
  )
  if (locationSpecialties.length > 0) {
    return locationSpecialties
  }
  return normalizeScreeningSpecialtyConstraintList(getClinicConfiguredSpecialties(clinicBrand))
}

export function isScreeningSpecialtyAllowedForClinic(
  targetSpecialtySlug,
  clinicLocation,
  clinicBrand,
) {
  const allowedSpecialties = resolveClinicAvailableSpecialtySlugs(clinicLocation, clinicBrand)
  if (allowedSpecialties.length === 0) {
    return true
  }
  return allowedSpecialties.includes(normalizeScreeningSpecialtyConstraint(targetSpecialtySlug))
}

export function filterScreeningTargetSpecialtyOptionsForClinic(
  options,
  clinicLocation,
  clinicBrand,
) {
  return (options || []).filter((option) =>
    isScreeningSpecialtyAllowedForClinic(option?.slug ?? option?.id, clinicLocation, clinicBrand),
  )
}

export function filterClinicServicesByAvailableSpecialties(
  clinicServices,
  clinicLocation,
  clinicBrand,
) {
  return (clinicServices || []).filter((service) => {
    if (service?.type !== 'screening') {
      return true
    }
    return isScreeningSpecialtyAllowedForClinic(
      service?.targetSpecialtySlug,
      clinicLocation,
      clinicBrand,
    )
  })
}
